import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { quickMeetingApi } from './api';
import { CALENDAR_SIGNAL, QM_POLL } from './constants';
import type { CalendarPrefsWire, NextMeetingDTO, QuickMeetingUnavailableReason, RecordingStatusDTO } from './types';

// The shell's Quick Meeting reads, in the shell's own QueryClient (the calendar remote's
// RTK Query cache can't reach it). The shell has no socket, so it polls (§18.4, §18.7).

export const quickMeetingKeys = {
  status: (userId: string | null, slug: string | null) =>
    ['shell', 'quick-meeting', 'status', userId, slug] as const,
  next: (userId: string | null, slug: string | null) =>
    ['shell', 'quick-meeting', 'next', userId, slug] as const,
  // Shared with sidebar/providers/calendarProviders.tsx: one cached copy of the preferences.
  preferences: (slug: string | null) => ['calendar', slug, 'preferences'] as const,
  extendOptions: (userId: string | null, slug: string | null, eventId: string) =>
    ['shell', 'quick-meeting', 'extend-options', userId, slug, eventId] as const,
};

/** After a failure: double the wait per consecutive failure, up to five minutes (§18.4). */
function withBackoff<T>(base: (data: T | undefined) => number) {
  return (query: { state: { fetchFailureCount: number; data: T | undefined } }) => {
    const failures = query.state.fetchFailureCount;
    const interval = base(query.state.data);
    return failures > 0 ? Math.min(QM_POLL.MAX_BACKOFF_MS, interval * 2 ** failures) : interval;
  };
}

/** No focus refetch while a poll is failing: a 5xx must not trigger the app outage probe. */
const focusUnlessFailing = (query: { state: { fetchFailureCount: number } }) =>
  query.state.fetchFailureCount === 0;

function useRefetchOn(events: readonly string[], refetch: () => unknown, enabled: boolean) {
  const key = events.join('|');
  useEffect(() => {
    if (!enabled) return;
    const names = key.split('|');
    const onSignal = () => void refetch();
    names.forEach((n) => window.addEventListener(n, onSignal));
    return () => names.forEach((n) => window.removeEventListener(n, onSignal));
  }, [key, refetch, enabled]);
}

/** The caller has takes of their own in flight, or results not yet seen. */
function hasOwnRows(d: RecordingStatusDTO | undefined): boolean {
  return !!d && (d.active != null || d.processing.length > 0 || d.recoverable.length > 0 || d.unseen.length > 0);
}

const statusInterval = withBackoff<RecordingStatusDTO>((d) =>
  d && (d.active || d.processing.length > 0) ? QM_POLL.STATUS_ACTIVE_MS : QM_POLL.STATUS_MS,
);

/**
 * Polls for a recorder, or anyone whose last answer still had takes (someone who lost
 * `recordings.create` keeps seeing their own processing ones). Everyone else reads it on
 * load, on focus and on the calendar's recordings signal.
 */
export function useRecordingStatusQuery(
  userId: string | null,
  slug: string | null,
  enabled: boolean,
  canRecord: boolean,
) {
  const on = enabled && !!userId && !!slug;
  const query = useQuery({
    queryKey: quickMeetingKeys.status(userId, slug),
    queryFn: () => quickMeetingApi.status(slug ?? ''),
    enabled: on,
    staleTime: 5_000,
    retry: false,
    refetchInterval: (q) => (canRecord || hasOwnRows(q.state.data) ? statusInterval(q) : false),
    refetchOnWindowFocus: focusUnlessFailing,
  });
  useRefetchOn([CALENDAR_SIGNAL.RECORDINGS_CHANGED], query.refetch, on);
  return query;
}

export function useNextMeetingQuery(userId: string | null, slug: string | null, enabled: boolean) {
  const on = enabled && !!userId && !!slug;
  const query = useQuery({
    queryKey: quickMeetingKeys.next(userId, slug),
    queryFn: () => quickMeetingApi.next(slug ?? ''),
    enabled: on,
    staleTime: 15_000,
    retry: false,
    refetchInterval: withBackoff<NextMeetingDTO>(() => QM_POLL.NEXT_MS),
    refetchOnWindowFocus: focusUnlessFailing,
  });
  useRefetchOn(
    [CALENDAR_SIGNAL.RECORDINGS_CHANGED, CALENDAR_SIGNAL.EVENTS_CHANGED],
    query.refetch,
    on,
  );
  return query;
}

export function useCalendarPrefsQuery(slug: string | null, enabled: boolean) {
  return useQuery({
    queryKey: quickMeetingKeys.preferences(slug),
    queryFn: () => quickMeetingApi.preferences(slug ?? ''),
    enabled: enabled && !!slug,
    staleTime: QM_POLL.PREFS_STALE_MS,
    retry: false,
    select: (d: CalendarPrefsWire) => ({
      h24: d?.time_format_24h === true,
      hideTitles: d?.hide_topbar_titles === true,
    }),
  });
}

/**
 * Mounted by the Extend menu's content, so it reads only while the menu is open. Nothing is
 * kept after it closes: a room can be booked in between, and stale "allowed" would mislead.
 */
export function useExtendOptionsQuery(userId: string | null, slug: string | null, eventId: string) {
  return useQuery({
    queryKey: quickMeetingKeys.extendOptions(userId, slug, eventId),
    queryFn: () => quickMeetingApi.extendOptions(slug ?? '', eventId),
    enabled: !!userId && !!slug,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

/** Refetch both shell reads after a write of ours. */
export function useInvalidateQuickMeeting(userId: string | null, slug: string | null) {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: quickMeetingKeys.status(userId, slug) });
    void client.invalidateQueries({ queryKey: quickMeetingKeys.next(userId, slug) });
  };
}

/** A start the server refused for a workspace reason flips the mic now; the next status read confirms it. */
export function useMarkQuickMeetingUnavailable(userId: string | null, slug: string | null) {
  const client = useQueryClient();
  return (reason: QuickMeetingUnavailableReason) =>
    client.setQueryData<RecordingStatusDTO>(quickMeetingKeys.status(userId, slug), (d) =>
      d ? { ...d, quick_meeting: { ...d.quick_meeting, available: false, reason } } : d,
    );
}
